import { Link } from "react-router-dom";
import { BookOpen, ArrowRight, Download } from "lucide-react";

import BROCHURES, { getBrochurePdfUrl } from "@/lib/brochureData";

// -----------------------------------------------------------------------------
//  HotelBrochureSection
//
//  Per-property brochure module rendered inside /hotels/:slug pages. It
//  looks up the matching Publuu flip-book from the same `brochureData.js`
//  catalogue that powers the /brochures hub — so operators keep a single
//  file to maintain.
//
//  The section auto-hides for properties that don't have a brochure yet
//  (Midelt, Aït Ben Haddou, some bivouacs) — no ugly placeholder.
//
//  Layout choices
//  ──────────────
//  • Light `#FDFBF7` background — provides a visual breath after the dark
//    curated gallery that precedes this section on the page.
//  • 12-col header (overline · title · intro) matching every other section
//    header.
//  • Single centred iframe with a max-width so the flip-book has breathing
//    room on ultra-wide monitors without going off-scale on mobile.
// -----------------------------------------------------------------------------

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || obj.es || "";
};

const COPY = {
  overline: { en: "BROCHURE", fr: "CATALOGUE", es: "CATÁLOGO" },
  title: {
    en: "The printed catalogue",
    fr: "Le catalogue imprimé",
    es: "El catálogo impreso",
  },
  intro: {
    en: "Browse the printed brochure of this property — every room, every corner, in a leaf-through experience.",
    fr: "Feuilletez le catalogue imprimé de cet établissement — chaque chambre, chaque recoin, dans une expérience à tourner les pages.",
    es: "Hojea el catálogo impreso de este alojamiento — cada habitación, cada rincón, en una experiencia página a página.",
  },
  cta: {
    en: "See all brochures",
    fr: "Voir tous les catalogues",
    es: "Ver todos los catálogos",
  },
  download: {
    en: "Download PDF",
    fr: "Télécharger le PDF",
    es: "Descargar catálogo (PDF)",
  },
};

// Brand mark used inline before the accommodation name — same source as
// the site header/footer so future asset updates propagate here for free.
const XALUCA_LOGO_URL =
  "https://customer-assets.emergentagent.com/job_c24999ff-c14f-412e-baf4-b11c0020fcc2/artifacts/zezo764i_Grup_Xaluca_logo.png.webp";

const HotelBrochureSection = ({ hotelSlug, hotelName, lang = "en" }) => {
  const brochure = BROCHURES.find((b) => b.hotelSlug === hotelSlug);

  // Silent no-op for hotels without a brochure — keeps the page free of
  // empty modules while the catalogue is still being produced.
  if (!brochure) return null;

  const name = pick(brochure.name, lang) || hotelName;
  const pdfUrl = getBrochurePdfUrl(brochure);

  return (
    <section
      id="brochure"
      className="py-20 md:py-28 bg-[#FDFBF7]"
      data-testid="section-hotel-brochure"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header — same editorial grammar as the sibling sections. */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-10 md:mb-14">
          <div className="md:col-span-5">
            <p className="overline text-[#C16542]">{pick(COPY.overline, lang)}</p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-tight text-[#2C2621]">
              {pick(COPY.title, lang)}
            </h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 self-end text-base md:text-lg text-[#5C5248] leading-relaxed">
            {pick(COPY.intro, lang)}
          </p>
        </div>

        {/* Embedded flip-book. Max-width keeps the composition centered on
            ultra-wide monitors; the fluid height ladder mirrors the
            /brochures cards for visual continuity. */}
        <div
          className="mx-auto max-w-5xl bg-white border border-[#E8DFCF] shadow-[0_4px_20px_-12px_rgba(44,38,33,0.15)] overflow-hidden"
          data-testid="hotel-brochure-frame"
        >
          <div className="px-6 py-4 border-b border-[#E8DFCF]/70 flex items-center gap-3 md:gap-4">
            {/* Brand mark — same monogram used across header/footer, sized
                to match the cap-height of the accommodation title so the
                pair reads as a single, harmonious signature. `data-no-
                xaluca-logo="true"` opts this specific image out of the
                automatic corner-watermark overlay. */}
            <img
              src={XALUCA_LOGO_URL}
              alt=""
              aria-hidden="true"
              data-no-xaluca-logo="true"
              loading="lazy"
              decoding="async"
              className="shrink-0 w-9 h-9 md:w-11 md:h-11 object-contain"
              data-testid="hotel-brochure-logo-mark"
            />
            <h3
              className="font-serif-x text-2xl md:text-3xl leading-tight text-[#2C2621] truncate"
              title={name}
            >
              {name}
            </h3>
          </div>
          <iframe
            src={brochure.publuuUrl}
            title={name}
            scrolling="no"
            frameBorder="0"
            allow="clipboard-write; autoplay; fullscreen"
            allowFullScreen
            loading="lazy"
            className="w-full block h-[340px] sm:h-[420px] md:h-[500px] lg:h-[560px]"
            data-testid="hotel-brochure-iframe"
          />
        </div>

        {/* CTA cluster — primary sends the visitor to the full library;
            secondary offers a direct PDF download for offline / print
            usage. The download link opens in a new tab because Publuu
            handles the actual file streaming client-side. */}
        <div className="mt-10 md:mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/brochures"
            className="inline-flex items-center gap-2 bg-[#2C2621] text-[#FDFBF7] px-6 py-3.5 text-xs tracking-[0.3em] uppercase hover:bg-[#C16542] transition-colors"
            data-testid="hotel-brochure-see-all"
          >
            <BookOpen className="w-4 h-4" strokeWidth={1.5} />
            {pick(COPY.cta, lang)}
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
          {pdfUrl ? (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-[#2C2621] text-[#2C2621] px-6 py-3.5 text-xs tracking-[0.3em] uppercase hover:bg-[#2C2621] hover:text-[#FDFBF7] transition-colors"
              data-testid="hotel-brochure-download"
            >
              <Download className="w-4 h-4" strokeWidth={1.5} />
              {pick(COPY.download, lang)}
            </a>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default HotelBrochureSection;
